/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { NextResponse } from "next/server";

/**
 * Result type from processLocale() — a union of void, redirect Response, or NextResponse rewrite.
 * Exported for type annotations in middleware files.
 */
export type ProcessLocaleResult = Response | NextResponse | void;

/**
 * Determines if a processLocale() result is a redirect that should
 * short-circuit the middleware pipeline.
 *
 * processLocale() returns three kinds of results:
 * - `void` — no locale action needed; continue normally
 * - `NextResponse.rewrite()` (status 200) — internal URL rewrite; continue through auth/CSRF
 * - `Response.redirect()` (status 3xx) — actual redirect; return immediately
 *
 * Only redirects should cause an early return. Rewrites must continue through
 * the full middleware pipeline (auth, CSRF, etc.) because the user stays on the
 * same URL and the response is the final one delivered to the browser.
 *
 * @example
 * ```ts
 * const localeRes = processLocale(request, locales, defaultLocale);
 * if (isLocaleRedirect(localeRes)) {
 *   return localeRes; // 3xx redirect — skip auth/CSRF (next request will run full pipeline)
 * }
 * const response = createBaseResponse(localeRes);
 * await middlewareAuth(request, response);
 * await middlewareCSRF(response);
 * ```
 */
export function isLocaleRedirect(localeRes: ProcessLocaleResult): localeRes is Response {
	return !!localeRes && localeRes.status >= 300 && localeRes.status < 400;
}

/**
 * Creates the base NextResponse for the middleware pipeline after locale processing.
 *
 * - If processLocale() returned a rewrite (`NextResponse`), reconstructs it with the
 *   provided request init so headers are available via `headers()` in Server Components.
 * - If processLocale() returned void, creates a fresh `NextResponse.next()`.
 *
 * This must be called AFTER `isLocaleRedirect()` has filtered out redirect responses.
 *
 * @param localeRes - The result from processLocale() (after redirects have been handled)
 * @param init - Optional request init passed to NextResponse.next() / NextResponse.rewrite()
 *               for propagating request headers to Server Components
 *
 * @example
 * ```ts
 * const localeRes = processLocale(request, locales, defaultLocale);
 * if (isLocaleRedirect(localeRes)) return localeRes;
 *
 * const response = createBaseResponse(localeRes, { request: { headers: reqHeaders } });
 * ```
 */
export function createBaseResponse(
	localeRes: ProcessLocaleResult,
	init?: { request: { headers: Headers } },
): NextResponse {
	if (localeRes instanceof NextResponse) {
		// Locale handler returned a rewrite — reconstruct with request headers
		// so they're accessible via headers() in Server Components
		const rewriteTarget = localeRes.headers.get("x-middleware-rewrite");
		if (rewriteTarget && init) {
			return NextResponse.rewrite(rewriteTarget, init);
		}
		return localeRes;
	}

	return init ? NextResponse.next(init) : NextResponse.next();
}
